import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  Badge,
  Icon,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import {
  FaDatabase,
  FaShieldAlt,
  FaExchangeAlt,
  FaChartLine,
  FaRobot,
  FaCode,
} from "react-icons/fa";

import Footer from "../Common/Footer";
import Header from "../Common/Header";
import { handleCTA } from "../../utils";

// Define Integration Feature interface
interface IntegrationFeature {
  title: string;
  description: string;
  icon: any;
  benefits: string[];
}

// Integration Features Data
const INTEGRATION_FEATURES: IntegrationFeature[] = [
  {
    title: "FHIR API Integration",
    description:
      "Seamlessly connect with FHIR-compliant EHR systems using Google Cloud Healthcare API's FHIR store.",
    icon: FaDatabase,
    benefits: [
      "Create, retrieve, store, and search clinical data in FHIR format",
      "Support for major FHIR versions (R4, STU3, DSTU2)",
      "Bidirectional data synchronization",
      "Real-time patient data updates",
    ],
  },
  {
    title: "HL7v2 Message Processing",
    description:
      "Process and transform HL7v2 messages with Google's managed HL7v2 API.",
    icon: FaExchangeAlt,
    benefits: [
      "Ingest and process streaming clinical data",
      "Automatic message validation and parsing",
      "Support for all HL7v2 message types",
      "Real-time message transformation",
    ],
  },
  {
    title: "Healthcare NLP",
    description:
      "Extract medical insights from unstructured text using Google's Healthcare Natural Language API.",
    icon: FaRobot,
    benefits: [
      "Extract medical concepts and relationships",
      "Standardize medical terminology",
      "Identify clinical entities and relationships",
      "Support for multiple languages",
    ],
  },
  {
    title: "Security & Compliance",
    description:
      "Enterprise-grade security and compliance features built on Google Cloud.",
    icon: FaShieldAlt,
    benefits: [
      "HIPAA compliance out of the box",
      "End-to-end encryption",
      "Fine-grained access control",
      "Audit logging and monitoring",
    ],
  },
  {
    title: "Analytics & Insights",
    description:
      "Transform healthcare data into actionable insights with BigQuery integration.",
    icon: FaChartLine,
    benefits: [
      "Real-time analytics dashboards",
      "Custom reporting capabilities",
      "Population health insights",
      "Performance metrics tracking",
    ],
  },
  {
    title: "Developer Tools",
    description:
      "Comprehensive SDKs and tools for custom integration development.",
    icon: FaCode,
    benefits: [
      "REST and RPC APIs",
      "Client libraries for multiple languages",
      "Comprehensive documentation",
      "Sample applications and quickstarts",
    ],
  },
];

// Integration Partners
// const INTEGRATION_PARTNERS = [
//   { name: "Epic", logo: "/assets/logos/epic.png" },
//   { name: "Cerner", logo: "/assets/logos/cerner.png" },
//   { name: "Athenahealth", logo: "/assets/logos/athena.png" },
//   { name: "Meditech", logo: "/assets/logos/meditech.png" },
//   { name: "Allscripts", logo: "/assets/logos/allscripts.png" },
// ];

function SmartEHRIntegration() {
  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box>
      <Header minimal={false} />

      {/* Hero Section */}
      <Box bg={sectionBg} py={{ base: 16, md: 24 }}>
        <Container maxW="container.lg" textAlign="center">
          <Badge
            colorScheme="teal"
            px={3}
            py={1}
            mb={4}
            borderRadius="full"
            fontSize="sm"
          >
            Enterprise Integration
          </Badge>
          <Heading as="h1" size="2xl" mb={4} fontWeight="bold">
            Smart EHR Integration
          </Heading>
          <Text fontSize="lg" color={useColorModeValue("gray.600", "gray.400")}>
            Seamlessly connect with major EHR systems using Google Cloud
            Healthcare API. Transform healthcare data into actionable insights
            while maintaining enterprise-grade security and compliance.
          </Text>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxW="container.2xl" py={16} px={{ base: 4, md: 8 }}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {INTEGRATION_FEATURES.map((feature, idx) => (
            <Box
              key={idx}
              bg={cardBg}
              borderRadius="xl"
              boxShadow="md"
              p={8}
              borderWidth={1}
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "lg",
              }}
            >
              <VStack spacing={4} align="start">
                <Icon as={feature.icon} boxSize={8} color="teal.500" />
                <Heading as="h3" size="md">
                  {feature.title}
                </Heading>
                <Text color={useColorModeValue("gray.600", "gray.400")}>
                  {feature.description}
                </Text>
                <List spacing={2}>
                  {feature.benefits.map((benefit, idx) => (
                    <ListItem key={idx} display="flex" alignItems="center">
                      <ListIcon
                        as={IoMdCheckmarkCircleOutline}
                        color="green.500"
                      />
                      <Text fontSize="sm">{benefit}</Text>
                    </ListItem>
                  ))}
                </List>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Integration Partners */}
      {/* <Box bg={sectionBg} py={16}>
        <Container maxW="container.lg">
          <VStack spacing={8}>
            <Heading as="h2" size="xl" textAlign="center">
              Trusted by Leading Healthcare Systems
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={8}>
              {INTEGRATION_PARTNERS.map((partner, idx) => (
                <Box
                  key={idx}
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="sm"
                >
                  <Image
                    src={partner.logo}
                    alt={partner.name}
                    maxH="40px"
                    objectFit="contain"
                  />
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box> */}

      {/* CTA Section */}
      <Box py={16}>
        <Container maxW="container.lg" textAlign="center">
          <VStack spacing={6}>
            <Heading as="h2" size="xl">
              Ready to Integrate?
            </Heading>
            <Text
              fontSize="lg"
              color={useColorModeValue("gray.600", "gray.400")}
            >
              Connect with our team to discuss your EHR integration needs and
              discover how we can help transform your healthcare data.
            </Text>
            <Button
              size="lg"
              colorScheme="teal"
              onClick={() => handleCTA("contact_sales")}
            >
              Contact Sales
            </Button>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default SmartEHRIntegration;
